import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { Group, TimeUnit, ComponentDefinition, ApprovalStatus, ISaveApprovalStatusDto, IApproval, IApprovalStrategy, IFrequency, Dictionary, IApprovalRow, Interval } from '../../models';
import { IGroupApprovalService, ILoggerService, IIntervalService, ICurrentApplicationService } from '../../services';
import { IRootScope } from '../../IRootScope';
import { refreshClick } from '../../common/events';
import { ApprovalJustificationGetter } from '../modals';
import { momentFormat } from '../../common';

class IndividualGroupApprovalController implements angular.IComponentController {
    columns: any[];
    ctrl: any;
    dateFormat: string;
    getApprovalJustification: ApprovalJustificationGetter;
    histories: { [date: string]: string };
    isLoading: boolean;
    selectedDate: string;
    selectedRow: IApprovalRow;
    workingDates: any[];
    workingRow: IApprovalRow;

    readonly groupDic: Dictionary<Group>;
    readonly isDisabled: boolean;
    readonly strategyDic: Dictionary<IApprovalStrategy>;

    private _appFrequency: IFrequency;
    private _isMonthMode: boolean;
    private _toSave: { [timestamp: string]: ISaveApprovalStatusDto };

    static $inject = [
        '$scope',
        '$rootScope',
        '$q',
        'groupApprovalService',
        'intervalService',
        'currentApplicationService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$q: angular.IQService,
        private _groupApprovalService: IGroupApprovalService,
        private _intervalService: IIntervalService,
        private _currentApplicationService: ICurrentApplicationService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.ctrl = {
            isDirty: false,
            cancel: () => this.reset(),
            save: () => this.save()
        };

        const app = this._currentApplicationService.getApplicationConfiguration();
        this._appFrequency = {
            interval: app.interval,
            intervalTimeUnit: app.intervalTimeUnit
        };

        this._$scope.$watch(() => this.selectedRow, () => {
            this.reset();
        });
    }

    changeStatus(approval, status: number, timestamp): angular.IPromise<void> {
        if (!approval.changed) {
            if (approval.status !== status) {
                return this.applyStatus(approval, status, timestamp);
            }
        } else {
            return this.revertStatus(approval, timestamp);
        }
        return this._$q.when();
    }

    isSelectedManual(status): boolean {
        let hasManual = false;

        this.columns.forEach((c, i) => {
            hasManual = hasManual || (c.isSelection && status.values[i].comment === 'Manual');
        });

        return hasManual;
    }

    private revertStatus(approval: IApproval, timestamp: string): angular.IPromise<void> {
        delete this._toSave[timestamp];
        approval.status = approval.original;
        approval.changed = false;
        this.ctrl.isDirty = Object.keys(this._toSave).length > 0;
        return this._$q.when();
    }

    private applyStatus(approval: IApproval, status: ApprovalStatus, timestamp: Date): angular.IPromise<void> {
        return this._groupApprovalService
            .isJustificationRequired(approval.level, status, this.workingRow.strategyGuid)
            .then(isRequired => {
                if (isRequired) {
                    this.getApprovalJustification(status === ApprovalStatus.Rejected, this.workingRow.group.uniqueName, moment(timestamp).format(momentFormat))
                        .then(comment => {
                            this.applyJustifiedStatus(approval, status, timestamp, comment);
                        })
                        .catch(() => {
                            // no comment was entered, don't do anything
                        });
                } else {
                    this.applyJustifiedStatus(approval, status, timestamp, undefined);
                }
            });
    }

    private applyJustifiedStatus(approval, status, timestamp, comment) {
        // for UI display
        approval.changed = true;
        approval.original = approval.status;
        approval.status = status;
        this.ctrl.isDirty = true;

        // for saving
        this._toSave[timestamp] = {
            groupGuid: this.workingRow.group.guid,
            status: status,
            level: approval.level,
            beginDate: timestamp,
            endDate: moment(timestamp).add(1, 'd').toISOString(),
            comment: comment
        };
    }

    private reset(): angular.IPromise<void> {
        this.isLoading = true;
        this._isMonthMode = false;

        if (this.selectedRow.promise) {
            return this.selectedRow.promise
                .then(data => {
                    this.workingRow = angular.copy(data[0]);
                    return this.setMode(this.workingRow.group);
                })
                .then(() => {
                    this._toSave = {};
                    this.ctrl.isDirty = false;

                    this.histories = {};
                    if (this.workingRow.histories?.length > 0) {
                        this.workingRow.histories.forEach(h => {
                            this.histories[moment(h.timeStamp).toISOString()] = h.approvals;
                        });
                    }

                    const allowInFuture = this.strategyDic[this.workingRow.strategyGuid]?.allowInFuture ?? false;
                    const interval = this._intervalService.getGroupInterval(this.workingRow.group, this._appFrequency);

                    if (this.workingRow.dates && this.workingRow.dates.length > 0) {
                        this.populateDateInformation(interval, allowInFuture);
                    }

                    // choose if we work on evey dates, or if we filter some according to mode
                    this.workingDates = (this._isMonthMode)
                        ? [this.workingRow.dates[0]]
                        : this.workingRow.dates;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        } else {
            this.isLoading = false;
        }
    }

    private populateDateInformation(interval: Interval, allowInFuture: boolean) {
        this.workingRow.dates.forEach(d => {
            const beginDate = moment(d.timeStamp);
            const endDate = beginDate.clone().add(interval.interval, interval.unit);
            const preventFuture = !allowInFuture && endDate.isAfter(moment());

            d.hasData = _(d.values)
                .map('value')
                .filter(v => v !== null)
                .value()
                .length > 0;
            d.beginDate = beginDate.toISOString();
            d.endDate = endDate.toISOString();
            const label = !d.hasData ? 'No data' : '';
            d.title = preventFuture ? 'Data can not be approved in the future' : label;
            d.isDisabled = (!d.hasData && !this.currentStatusIsApproved(d)) || preventFuture;
            d.isApprovalDisabled = !d.hasData;
        });
    }

    private currentStatusIsApproved(data: any): boolean {
        let isApproved: boolean = false;
        if (data && data.approvals && data.approvals.length > 0) {
            data.approvals.forEach((approval: IApproval, index: number) => {
                if (approval.status === ApprovalStatus.WaitingForApproval && approval.level > 0) {
                    isApproved = true;
                }
                if (approval.status === ApprovalStatus.Approved && index === (data.approvals.length - 1) && approval.canReject) {
                    isApproved = true;
                }
            });
        }
        return isApproved;
    }

    private setMode(g: { guid: string }): void {
        const group = this.groupDic[g.guid];
        this.dateFormat = 'yyyy-MM-dd';
        this._isMonthMode = false;

        if (group) {
            this._isMonthMode = group.frequency.interval === 1 &&
                group.frequency.intervalTimeUnit === TimeUnit.Month;

            if (this._isMonthMode) {
                this.dateFormat = 'yyyy-MM';
            }
        }
    }

    private save(): void {
        const array = this.getDataToSaveAccordingToMode();
        this._groupApprovalService.saveStatuses(array)
            .then(message => {
                if (!!message) {
                    this._loggerService.logError(message);
                } else {
                    this._loggerService.logSuccess('Saved');
                    this._$rootScope.$broadcast(refreshClick);
                }
            })
            .catch(reason => {
                this._loggerService.logError('Save of approbation failed.', true, null, String(reason.message ? reason.message : reason));
            });
    }

    private getDataToSaveAccordingToMode(): ISaveApprovalStatusDto[] {
        const ret = _.map(this._toSave, t => t);

        if (this._isMonthMode && ret && ret.length > 0) {
            ret.forEach(r => {
                r.endDate = moment(r.beginDate).add(1, 'M').toISOString();
            });
        }
        return ret;
    }
}

export const wtIndividualGroupApproval: ComponentDefinition = {
    componentId: 'wtIndividualGroupApproval',
    controller: IndividualGroupApprovalController,
    template: require('./individualGroupApproval.component.html'),
    controllerAs: 'vm',
    bindings: {
        columns: '=',
        ctrl: '=',
        getApprovalJustification: '<',
        groupDic: '<',
        isDisabled: '<',
        selectedDate: '=?',
        selectedRow: '=',
        strategyDic: '<'
    }
};